import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { buildPropertyPhotoUrl } from 'components/marketplace/property/helpers';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';
import { Popup } from 'react-map-gl/mapbox';

import { browseListingStatusLabel } from '../helpers/browseListingStatus';
import { ListingStatusBadge } from '../shared/ListingStatusBadge';
import { streetViewLocationFor, StreetViewPhoto } from '../shared/StreetViewPhoto';
import { MAP_MARKER_HEIGHT, MAP_MARKER_POPUP_CLEARANCE_WIDTH } from './mapMarkerImage';
import { getPopupPlacement } from './popupPlacement';
import type { PixelPoint, PixelSize, PopupPlacement } from './popupPlacement';
import type { MapMarkerProperty } from './types';

import styles from './MapMarker.module.css';

const POPUP_EDGE_PADDING = 8;
const POPUP_MARKER_GAP = 6;
const UNPOSITIONED_OFFSET: [number, number] = [0, -20];
const MAX_MARKER_SIZE: PixelSize = {
  height: MAP_MARKER_HEIGHT,
  width: MAP_MARKER_POPUP_CLEARANCE_WIDTH,
};

type PopupMap = {
  getContainer: () => HTMLElement;
  off: (event: 'move' | 'resize', listener: () => void) => unknown;
  on: (event: 'move' | 'resize', listener: () => void) => unknown;
  project: (lngLat: [number, number]) => PixelPoint;
};

function mapContainerSize(map: PopupMap): PixelSize {
  const container = map.getContainer();
  return { height: container.clientHeight, width: container.clientWidth };
}

function popupPriceLabel(property: MapMarkerProperty) {
  if (property.listPriceCents != null) {
    return `$${(property.listPriceCents / 100).toLocaleString()}`;
  }
  return property.listingState === 'AVAILABLE_SOON' ? 'Available soon' : 'Price not available';
}

function buildPhotoSlots(photos: readonly string[] | null | undefined) {
  const [primary, secondary, tertiary] = photos ?? [];
  return {
    primary: buildPropertyPhotoUrl(primary, { preset: '3:2-sm' }),
    secondary: buildPropertyPhotoUrl(secondary, { preset: '3:2-xs' }),
    tertiary: buildPropertyPhotoUrl(tertiary, { preset: '3:2-xs' }),
  };
}

export function buildMarkerAriaLabel(property: MapMarkerProperty) {
  const parts: string[] = [];
  if (property.listPriceCents != null) {
    parts.push(`Property listed at $${(property.listPriceCents / 100).toLocaleString()}`);
  } else if (property.listingState === 'AVAILABLE_SOON') {
    parts.push('Available soon property');
  } else {
    parts.push('Property');
  }
  const beds = property.bedrooms != null ? `${property.bedrooms} beds` : null;
  const baths = property.bathrooms != null ? `${property.bathrooms} baths` : null;
  const summary = [beds, baths].filter(Boolean).join(', ');
  if (summary) {
    parts.push(summary);
  }
  const statusLabel = browseListingStatusLabel(property.displayBadge);
  if (statusLabel) {
    parts.push(statusLabel);
  }
  if (property.isLiked) {
    parts.push('favorited');
  }
  return parts.join(', ');
}

type PopupImageProps = {
  uri: string | null;
  alt: string;
  className: string;
};

const PopupImage = ({ uri, alt, className }: PopupImageProps) => {
  return (
    <PropertyPhotoImage
      alt={alt}
      src={uri}
      containerClassName={className}
      className="h-full w-full object-cover"
    />
  );
};

type PopupBodyProps = {
  animate: boolean;
  property: MapMarkerProperty;
};

const PopupBody = ({ animate, property }: PopupBodyProps) => {
  const slots = buildPhotoSlots(property.photoUrls);
  const streetViewLocation = streetViewLocationFor({
    availableSoon: property.listingState === 'AVAILABLE_SOON',
    photoCount: [slots.primary, slots.secondary, slots.tertiary].filter(Boolean).length,
    latitude: property.latitude,
    longitude: property.longitude,
  });

  return (
    <Col className={animate ? styles.popupEnter : undefined}>
      <Box className="relative">
        {streetViewLocation ? (
          <StreetViewPhoto
            latitude={streetViewLocation.latitude}
            longitude={streetViewLocation.longitude}
            alt={`Street view of ${property.street ?? 'this home'}`}
            className="aspect-[7/3] w-88 rounded-t-2xl"
            fallbackLabel="Photos coming soon"
          />
        ) : (
          <PopupImage
            uri={slots.primary}
            alt="map-marker-primary-property-image"
            className="aspect-[7/3] w-88 rounded-t-2xl"
          />
        )}
        <Box className="pointer-events-none absolute top-2 left-2">
          <ListingStatusBadge displayBadge={property.displayBadge} />
        </Box>
      </Box>
      {!streetViewLocation && (slots.secondary || slots.tertiary) && (
        <Row className="justify-between pt-1.5">
          {slots.secondary ? (
            <PopupImage
              uri={slots.secondary}
              alt="map-marker-secondary-property-image"
              className="aspect-[43/25] w-43"
            />
          ) : null}
          {slots.tertiary ? (
            <PopupImage
              uri={slots.tertiary}
              alt="map-marker-tertiary-property-image"
              className="aspect-[43/25] w-43"
            />
          ) : null}
        </Row>
      )}
      <Col className="gap-1.5 px-5 pb-4 pt-5">
        <Row className="items-baseline justify-between">
          <Text as="p" className="text-label-xl text-od-content-primary">
            {popupPriceLabel(property)}
          </Text>
          {property.isLiked ? <FavoriteHeartIcon filled size={14} className="self-center" /> : null}
        </Row>
        {(() => {
          const stats = [
            property.bedrooms != null ? `${property.bedrooms} bds` : null,
            property.bathrooms != null ? `${property.bathrooms} ba` : null,
            property.totalLivingSquareFootage != null
              ? `${property.totalLivingSquareFootage.toLocaleString()} sqft`
              : null,
          ].filter((part): part is string => part !== null);
          if (stats.length === 0) {
            return null;
          }
          return (
            <Row className="items-baseline">
              {stats.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 ? (
                    <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                      {' • '}
                    </Text>
                  ) : null}
                  <Text as="span" className="text-body-sm text-od-content-secondary">
                    {part}
                  </Text>
                </Fragment>
              ))}
            </Row>
          );
        })()}
        {property.street ? (
          <Text as="p" className="truncate text-body-sm text-od-content-tertiary">
            {property.street}
          </Text>
        ) : null}
      </Col>
    </Col>
  );
};

type MapMarkerPopupProps = {
  map: PopupMap;
  popupId: string;
  property: MapMarkerProperty;
};

export const MapMarkerPopup = ({ map, popupId, property }: MapMarkerPopupProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const popupSizeRef = useRef<PixelSize | undefined>(undefined);
  const [mapSize, setMapSize] = useState(() => mapContainerSize(map));
  const [placement, setPlacement] = useState<PopupPlacement>();
  const popupStyle = useMemo<CSSProperties>(
    () => ({
      visibility: placement ? 'visible' : 'hidden',
    }),
    [placement],
  );

  const updatePlacement = useCallback(() => {
    const popupSize = popupSizeRef.current;
    if (!popupSize) {
      return;
    }
    const markerPoint = map.project([property.longitude, property.latitude]);
    const nextPlacement = getPopupPlacement({
      edgePadding: POPUP_EDGE_PADDING,
      mapSize,
      markerGap: POPUP_MARKER_GAP,
      markerPoint,
      markerSize: MAX_MARKER_SIZE,
      popupSize,
    });
    setPlacement((current) =>
      current?.anchor === nextPlacement.anchor &&
      current.offset[0] === nextPlacement.offset[0] &&
      current.offset[1] === nextPlacement.offset[1]
        ? current
        : nextPlacement,
    );
  }, [map, mapSize, property.latitude, property.longitude]);

  const measureAndUpdatePlacement = useCallback(() => {
    const popupContent = contentRef.current?.closest('.mapboxgl-popup-content');
    if (!popupContent) {
      return;
    }
    const { height, width } = popupContent.getBoundingClientRect();
    if (height === 0 || width === 0) {
      return;
    }
    popupSizeRef.current = { height, width };
    updatePlacement();
  }, [updatePlacement]);

  useEffect(() => {
    const container = map.getContainer();
    const updateMapSize = () => {
      const nextSize = mapContainerSize(map);
      setMapSize((current) =>
        current.height === nextSize.height && current.width === nextSize.width ? current : nextSize,
      );
    };
    updateMapSize();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(updateMapSize);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [map]);

  useEffect(() => {
    measureAndUpdatePlacement();
    const popupContent = contentRef.current?.closest('.mapboxgl-popup-content');
    const observer =
      popupContent && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measureAndUpdatePlacement)
        : undefined;
    if (popupContent) {
      observer?.observe(popupContent);
    }
    map.on('move', updatePlacement);
    map.on('resize', updatePlacement);
    return () => {
      observer?.disconnect();
      map.off('move', updatePlacement);
      map.off('resize', updatePlacement);
    };
  }, [map, measureAndUpdatePlacement, updatePlacement]);

  return (
    <Popup
      anchor={placement?.anchor ?? 'bottom'}
      longitude={property.longitude}
      latitude={property.latitude}
      offset={placement?.offset ?? UNPOSITIONED_OFFSET}
      closeButton={false}
      maxWidth="none"
      onOpen={measureAndUpdatePlacement}
      style={popupStyle}
    >
      <Box ref={contentRef} id={popupId} role="region">
        <PopupBody animate={placement !== undefined} property={property} />
      </Box>
    </Popup>
  );
};
